import React from "react";
import { useNavigate } from "react-router-dom";
import "./HeroSection.css";

import { motion, AnimatePresence } from "motion/react";
import {
  MessageSquare,
  Users,
  ShoppingCart,
  GraduationCap,
  Bot,
  User,
} from "lucide-react";

const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 2l1.9 5.8L20 9.7l-5.1 3.7L16.8 20 12 16.9 7.2 20l1.9-6.6L4 9.7l6.1-1.9L12 2z"
    />
  </svg>
);
const ArrowRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M13 5l7 7-7 7v-4H4v-6h9V5z" />
  </svg>
);
const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M8 5v14l11-7z" />
  </svg>
);

const agents = [
  {
    id: "support",
    icon: MessageSquare,
    title: "AI Support Agent",
    subtitle: "24x7 Customer Support",
    theme: "blue",
    conversation: [
      { type: "user", message: "I'd like to cancel my subscription and get a refund." },
      {
        type: "bot",
        message:
          "I can help with that. You're eligible since it's been only 5 days since your subscription started.",
      },
      {
        type: "bot",
        message:
          "I've initiated the cancellation process and your refund will be processed within 3-5 business days.",
      },
    ],
  },
  {
    id: "employee",
    icon: Users,
    title: "AI Employee Agent",
    subtitle: "Automated Employee Support",
    theme: "purple",
    conversation: [
      { type: "user", message: "Hi, could you tell me how many earned leaves I have left?" },
      {
        type: "bot",
        message:
          "You've used 7 leaves so far this year. You have 8 remaining out of your total 15 annual leaves.",
      },
      { type: "bot", message: "Would you like to apply for leave? I can help you with that." },
    ],
  },
  {
    id: "sales",
    icon: ShoppingCart,
    title: "AI Sales Agent",
    subtitle: "Conversational Commerce",
    theme: "orange",
    conversation: [
      { type: "user", message: "I'm looking for a white/grey t-shirt made of cotton." },
      { type: "bot", message: "Absolutely, we have several great options for you!" },
      {
        type: "bot",
        message:
          "Here's our Cotton Comfort T-Shirt in Light Grey - 100% organic cotton, available in sizes S-XXL. It's currently 20% off at $23.99.",
      },
    ],
  },
  {
    id: "education",
    icon: GraduationCap,
    title: "Education Copilot",
    subtitle: "AI Copilots trained on your Data",
    theme: "green",
    conversation: [
      {
        type: "user",
        message:
          "Do you offer undergrad courses in AI and software engineering for international students?",
      },
      {
        type: "bot",
        message:
          "Yes, our BSc in Software Engineering is perfect for you! It includes specialized modules in AI, Machine Learning, and Data Science.",
      },
      {
        type: "bot",
        message:
          "We welcome international students and offer full support with visa applications. Would you like to know more about admission requirements?",
      },
    ],
  },
];

function Stat({ icon, value, label }) {
  return (
    <div className="stat">
      <div className="stat-icon">{icon}</div>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

const HeroSection = () => {
  const navigate = useNavigate();

  const handleBuildChatbot = () => navigate("/BuildChatBot");
  const handleBookDemo = () => navigate("/bookdemo");

  // ===== interactive agents state (used only in cards section) =====
  const [activeAgent, setActiveAgent] = React.useState(agents[0]);
  const [displayedMessages, setDisplayedMessages] = React.useState(0);
  const timeoutsRef = React.useRef([]);

  const clearTimers = () => {
    timeoutsRef.current.forEach((t) => clearTimeout(t));
    timeoutsRef.current = [];
  };

  const startConversation = (agent) => {
    clearTimers();
    setActiveAgent(agent);
    setDisplayedMessages(0);

    timeoutsRef.current.push(setTimeout(() => setDisplayedMessages(1), 500));
    timeoutsRef.current.push(setTimeout(() => setDisplayedMessages(2), 1200));
    timeoutsRef.current.push(setTimeout(() => setDisplayedMessages(3), 2000));
  };

  React.useEffect(() => {
    startConversation(agents[0]);
    return () => clearTimers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* ===== HERO ===== */}
      <section className="hero container">
        <h1 className="hero-title">
          <span className="ia-titleGradient">AI Assistants</span> that answer accurately
          <br /> and help you get work done
        </h1>

        <p className="hero-subtitle">
          Connect your website, docs, and help center to XenaChat to reduce repetitive questions, capture leads and{" "}
          <span className="accent">support customers 24/7</span> with replies grounded in your content.
        </p>

        <div className="cta-row">
          <button className="handleButton" onClick={handleBuildChatbot}>
            BUILD YOUR ASSISTANT <ArrowRightIcon />
          </button>
          <button className="demobutton" onClick={handleBookDemo}>
             SEE A LIVE DEMO
          </button>
        </div>
      </section>

      {/* ===== CARDS ===== */}
      <section className="cards container">
        <div className="ia-shell">
          <div className="ia-bg" aria-hidden="true">
            <div className="ia-grid" />
          </div>

          <div className="ia-inner">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="ia-head"
            >
              <h2 className="ia-title">
                Choose Your{" "}
                <span className="ia-titleGradient">AI Agent</span>
              </h2>
              <p className="ia-subtitle">
                See them in action — click to switch between different agent types
              </p>
            </motion.div>

            <div className="ia-layout">
              {/* Left: selector */}
              <div className="ia-selector">
                {agents.map((agent, index) => {
                  const Icon = agent.icon;
                  const isActive = activeAgent.id === agent.id;

                  return (
                    <motion.button
                      key={agent.id}
                      initial={{ opacity: 0, x: -50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => startConversation(agent)}
                      className={`ia-agentBtn ${isActive ? "is-active" : ""}`}
                      type="button"
                    >
                      <div className="ia-agentRow">
                        <div className={`ia-iconBox ia-theme-${agent.theme}`}>
                          <Icon className="ia-icon" />
                        </div>

                        <div className="ia-agentText">
                          <h3 className="ia-agentTitle">{agent.title}</h3>
                          <p className="ia-agentSubtitle">{agent.subtitle}</p>
                        </div>

                        {isActive && (
                          <motion.div
                            layoutId="iaActiveIndicator"
                            className={`ia-activeDot ia-theme-${agent.theme}`}
                          />
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Right: chat */}
              <div className="ia-chatWrap">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="ia-chatCard"
                >
                  <div className={`ia-chatHeader ia-theme-${activeAgent.theme}`}>
                    <div className="ia-chatAvatar">
                      <Bot className="ia-chatAvatarIcon" />
                    </div>
                    <div>
                      <h4 className="ia-chatHeaderTitle">{activeAgent.title}</h4>
                      <p className="ia-chatHeaderSub">Online</p>
                    </div>
                  </div>

                  <div className="ia-chatBody">
                    <AnimatePresence mode="wait">
                      {activeAgent.conversation
                        .slice(0, displayedMessages)
                        .map((msg, idx) => (
                          <motion.div
                            key={`${activeAgent.id}-${idx}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4 }}
                            className={`ia-msgRow ${msg.type === "user" ? "is-user" : "is-bot"}`}
                          >
                            <div className="ia-msgStack">
                              {msg.type === "bot" && (
                                <div className={`ia-miniAvatar ia-theme-${activeAgent.theme}`}>
                                  <Bot className="ia-miniAvatarIcon" />
                                </div>
                              )}

                              <div
                                className={`ia-bubble ${
                                  msg.type === "user"
                                    ? "ia-bubbleUser"
                                    : `ia-bubbleBot ia-theme-${activeAgent.theme}`
                                }`}
                              >
                                <p className="ia-bubbleText">{msg.message}</p>
                              </div>

                              {msg.type === "user" && (
                                <div className="ia-miniAvatar ia-miniAvatarUser">
                                  <User className="ia-miniAvatarIconUser" />
                                </div>
                              )}
                            </div>
                          </motion.div>
                        ))}
                    </AnimatePresence>

                    {displayedMessages < activeAgent.conversation.length && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="ia-typingRow"
                      >
                        <div className={`ia-miniAvatar ia-theme-${activeAgent.theme}`}>
                          <Bot className="ia-miniAvatarIcon" />
                        </div>
                        <div className="ia-typingBubble">
                          <span className="ia-dot" style={{ animationDelay: "0ms" }} />
                          <span className="ia-dot" style={{ animationDelay: "150ms" }} />
                          <span className="ia-dot" style={{ animationDelay: "300ms" }} />
                        </div>
                      </motion.div>
                    )}
                  </div>

                  <div className="ia-chatFooter">
                    <div className="ia-inputRow">
                      <input
                        type="text"
                        placeholder="Type your message..."
                        className="ia-input"
                        disabled
                      />
                      <button
                        className={`ia-sendBtn ia-theme-${activeAgent.theme}`}
                        type="button"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

    <div className="leverage-text">
        <h2>
         Cut repetitive questions by up to{" "}
          <span className="gradient-soft percent">70% without losing the human touch</span>
        </h2>

        <p className="leverage-subtitle"> Deliver consistent answers based on your real content, and route complex cases to your team when needed.</p>
      </div>

      <div className="features">
        <div className="feature">
          <div className="feature-icon grad-green">
            <svg className="feature-svg" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 16.2l-3.5-3.5L4 14.2l5 5 11-11-1.5-1.5L9 16.2z" />
            </svg>
          </div>
          <div>
            <h4>Grounded responses</h4>
            <p>Answers are based on the pages you connect</p>
          </div>
        </div>

        <div className="feature">
         <div className="feature-icon grad-blue">
            <svg className="feature-svg" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 2a10 10 0 100 20 10 10 0 000-20zm7.5 10h-3.1c-.1 2.1-.7 4-1.6 5.5A8 8 0 0119.5 12zM12 4c1.2 0 2.9 2.3 3.3 6H8.7C9.1 6.3 10.8 4 12 4zM4.5 12h3.1c.1 2.1.7 4 1.6 5.5A8 8 0 014.5 12zm3.1-2H4.5a8 8 0 014.7-5.5c-.9 1.5-1.5 3.4-1.6 5.5zm8.8 0c-.1-2.1-.7-4-1.6-5.5A8 8 0 0119.5 10h-3.1zM12 20c-1.2 0-2.9-2.3-3.3-6h6.6c-.4 3.7-2.1 6-3.3 6zm-3.9-2.5A10.2 10.2 0 017.6 14H4.5a8 8 0 003.6 3.5zm8.3 0A8 8 0 0019.5 14h-3.1c-.1 1.2-.4 2.4-.8 3.5z"
              />
            </svg>
          </div>
          <div>
            <h4>Multilingual support</h4>
            <p>Assist users in 50+ languages</p>
          </div>
        </div>

        <div className="feature">
         <div className="feature-icon grad-purple">
          <svg className="feature-svg" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M13 2L3 14h7l-1 8 12-14h-7l-1-6z" />
          </svg>
        </div>
          <div>
            <h4>Works everywhere</h4>
            <p>Website widget, share link, and embeddable chat</p>
          </div>
        </div>
      </div>

    </>
  );
};

export default HeroSection;
